import React, { useState } from 'react';
import OfficerRow from './OfficerRow';

// Sort order: active-ish statuses first, historical last
const STATUS_SORT_ORDER = {
  active:    0,
  accepted:  1,
  open:      2,
  completed: 3,
  resolved:  4,
  paid:      5,
  disputed:  6,
  expired:   7,
  rejected:  8,
};

const HISTORICAL_STATUSES = ['expired', 'rejected'];

/**
 * OfficerRoster — presentational list container.
 *
 * Clicking a row calls onSelectOffer(offer) → parent pushes .../offer/:offerId
 * which opens OfferPanel without re-mounting ShiftDetailMaster.
 *
 * Props:
 *   offers          — Offer[] from ShiftDetail state
 *   selectedOfferId — string|null  (highlighted row)
 *   onSelectOffer   — (offer) => void
 *   onGoToJob       — (jobId) => void
 *   jobId           — string|null
 */
const OfficerRoster = ({ offers, selectedOfferId, onSelectOffer, onGoToJob, jobId }) => {
  const [showHistorical, setShowHistorical] = useState(false);

  const sorted = [...offers].sort(
    (a, b) => (STATUS_SORT_ORDER[a.status] ?? 99) - (STATUS_SORT_ORDER[b.status] ?? 99)
  );

  const active     = sorted.filter((o) => !HISTORICAL_STATUSES.includes(o.status));
  const historical = sorted.filter((o) =>  HISTORICAL_STATUSES.includes(o.status));

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      {/* Section header */}
      <div className="flex items-center justify-between bg-muted/20 border-b border-border px-4 py-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Officers&nbsp;
          <span className="text-foreground">({offers.length})</span>
        </p>
        {active.length === 0 && jobId && (
          <button
            type="button"
            onClick={() => onGoToJob(jobId)}
            className="text-[10px] font-medium text-brand-pink hover:underline"
          >
            Assign Officers →
          </button>
        )}
      </div>

      {/* Active / pending offers */}
      {active.length > 0 ? (
        <div>
          {active.map((offer) => (
            <OfficerRow
              key={offer._id?.toString()}
              offer={offer}
              isSelected={selectedOfferId === offer._id?.toString()}
              onSelect={onSelectOffer}
            />
          ))}
        </div>
      ) : (
        <div className="px-4 py-8 text-center">
          <p className="text-sm font-medium text-muted-foreground">No officers assigned yet</p>
          {jobId && (
            <button
              type="button"
              onClick={() => onGoToJob(jobId)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-pink px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-pink/90"
            >
              Find Officers for this Shift →
            </button>
          )}
        </div>
      )}

      {/* Historical section (expired / rejected) */}
      {historical.length > 0 && (
        <div className="border-t border-border">
          <button
            type="button"
            onClick={() => setShowHistorical((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/10 hover:bg-muted/20 transition-colors text-left"
          >
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              Previous attempts ({historical.length})
            </span>
            <span className="text-[10px] text-muted-foreground">
              {showHistorical ? 'Hide' : 'Show'} expired / declined
            </span>
          </button>
          {showHistorical && historical.map((offer) => (
            <OfficerRow
              key={offer._id?.toString()}
              offer={offer}
              isSelected={selectedOfferId === offer._id?.toString()}
              onSelect={onSelectOffer}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default OfficerRoster;
